import type {
  PaymentCtaSource,
  TrackEventFunction,
} from '@/lib/analytics/types';

type PaymentCtaTracking = {
  /** Which CTA was clicked. */
  source: PaymentCtaSource;
  /**
   * `trackEvent` from `useAnalytics()`. Injected rather than pulled from the
   * `analytics` singleton so the event carries the same user, course, session
   * and device enrichment as every other tracked CTA. Optional: callers
   * outside React (and tests) can skip it.
   */
  trackEvent?: TrackEventFunction;
};

/**
 * Opens the payment page in a new tab. No-op when the URL is missing
 * (e.g. the payment-status response hasn't loaded yet).
 *
 * The analytics event fires on every click, the no-op one included — a click
 * with `hasPaymentUrl: false` is a student who tried to pay and got nothing,
 * which is a failure we would otherwise never see. Reporting only successful
 * opens would read the absence as "nobody clicked".
 */
export const openPaymentUrl = (
  paymentUrl: string | null,
  tracking?: PaymentCtaTracking
): void => {
  tracking?.trackEvent?.('clickedPaymentCta', 1, {
    source: tracking.source,
    hasPaymentUrl: Boolean(paymentUrl),
  });

  if (paymentUrl) {
    window.open(paymentUrl, '_blank');
  }
};
